import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const ListBusiness = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    businessName: '',
    ownerName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    description: '',
    services: [],
    availability: [],
    licenses: '',
    insurance: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const serviceOptions = [
    'Emergency Repairs',
    'Preventive Maintenance',
    'Installation',
    'Equipment Upgrades',
    'Safety Inspections',
    'Cleaning Services',
  ];

  const availabilityOptions = [
    '24/7 Emergency',
    'Weekdays',
    'Weekends',
    'Business Hours',
    'After Hours',
    'By Appointment',
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    }
    
    if (!formData.ownerName.trim()) {
      newErrors.ownerName = 'Owner name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }
    
    if (!formData.zip.trim()) {
      newErrors.zip = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zip)) {
      newErrors.zip = 'Please enter a valid ZIP code';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Business description is required';
    }
    
    if (formData.services.length === 0) {
      newErrors.services = 'Please select at least one service';
    }
    
    if (formData.availability.length === 0) {
      newErrors.availability = 'Please select at least one availability option';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleCheckboxChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].includes(value)
        ? prev[type].filter(item => item !== value)
        : [...prev[type], value],
    }));
    // Clear error when user makes a selection
    if (errors[type]) {
      setErrors(prev => ({
        ...prev,
        [type]: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }

    try {
      setLoading(true);
      setSubmitStatus('submitting');
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSubmitStatus('success');
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      setSubmitStatus('error');
      setErrors(prev => ({
        ...prev,
        submit: 'Failed to submit form. Please try again.',
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="list-business-container">
      <div className="list-business-header">
        <h1>List Your Business</h1>
        <p>Join our network of trusted repair service providers</p>
      </div>

      <div className="list-business-content">
        <div className="benefits-section">
          <h2>WHY JOIN HOTSIDE REPAIRS?</h2>
          <div className="benefits-grid">
            <div className="benefit-item">
              <span className="benefit-icon">👥</span>
              <h3>REACH MORE CUSTOMERS</h3>
              <p>Connect with businesses looking for your services</p>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">📈</span>
              <h3>GROW YOUR BUSINESS</h3>
              <p>Increase your revenue with qualified leads</p>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">⭐</span>
              <h3>BUILD YOUR REPUTATION</h3>
              <p>Showcase your expertise and customer reviews</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="business-form">
          <div className="form-section">
            <h3>Business Information</h3>
            <div className="form-group">
              <label htmlFor="businessName">Business Name *</label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                className={errors.businessName ? 'error' : ''}
              />
              {errors.businessName && <span className="error-message">{errors.businessName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="ownerName">Owner Name *</label>
              <input
                type="text"
                id="ownerName"
                name="ownerName"
                value={formData.ownerName}
                onChange={handleChange}
                className={errors.ownerName ? 'error' : ''}
              />
              {errors.ownerName && <span className="error-message">{errors.ownerName}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={errors.phone ? 'error' : ''}
                />
                {errors.phone && <span className="error-message">{errors.phone}</span>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Location</h3>
            <div className="form-group">
              <label htmlFor="address">Street Address *</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className={errors.address ? 'error' : ''}
              />
              {errors.address && <span className="error-message">{errors.address}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">City *</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className={errors.city ? 'error' : ''}
                />
                {errors.city && <span className="error-message">{errors.city}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="state">State *</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className={errors.state ? 'error' : ''}
                />
                {errors.state && <span className="error-message">{errors.state}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="zip">ZIP Code *</label>
                <input
                  type="text"
                  id="zip"
                  name="zip"
                  value={formData.zip}
                  onChange={handleChange}
                  className={errors.zip ? 'error' : ''}
                />
                {errors.zip && <span className="error-message">{errors.zip}</span>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Services & Availability</h3>
            <div className="form-group">
              <label>Services Offered *</label>
              <div className="checkbox-group">
                {serviceOptions.map(service => (
                  <label key={service} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.services.includes(service)}
                      onChange={() => handleCheckboxChange('services', service)}
                    />
                    {service}
                  </label>
                ))}
              </div>
              {errors.services && <span className="error-message">{errors.services}</span>}
            </div>

            <div className="form-group">
              <label>Availability *</label>
              <div className="checkbox-group">
                {availabilityOptions.map(option => (
                  <label key={option} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.availability.includes(option)}
                      onChange={() => handleCheckboxChange('availability', option)}
                    />
                    {option}
                  </label>
                ))}
              </div>
              {errors.availability && <span className="error-message">{errors.availability}</span>}
            </div>
          </div>

          <div className="form-section">
            <h3>Additional Information</h3>
            <div className="form-group">
              <label htmlFor="description">Business Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={errors.description ? 'error' : ''}
                rows="4"
                placeholder="Tell us about your business, experience, and specialties..."
              />
              {errors.description && <span className="error-message">{errors.description}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="licenses">Licenses</label>
              <input
                type="text"
                id="licenses"
                name="licenses"
                value={formData.licenses}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="insurance">Insurance</label>
              <input
                type="text"
                id="insurance"
                name="insurance"
                value={formData.insurance}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Listing'}
            </button>
            {submitStatus === 'error' && (
              <div className="submit-error">
                {errors.submit || 'There was an error submitting the form. Please try again.'}
              </div>
            )}
            {submitStatus === 'success' && (
              <div className="submit-success">
                Listing submitted successfully! Redirecting...
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ListBusiness;
